import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import {
  Assignment as EpicIcon,
  ViewList as FeatureIcon,
  BugReport as BugIcon,
  Task as TaskIcon,
  Assignment as PBIIcon,
} from '@mui/icons-material';
import { formatDate } from '../../utils/formatters';
import StatusBadge from '../common/StatusBadge';
import ProgressBar from '../common/ProgressBar';

const ADO_BASE_URL = 'https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit';

function getWorkItemIcon(workItemType) {
  switch (workItemType) {
    case 'Epic':
      return <EpicIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    case 'Feature':
      return <FeatureIcon fontSize="small" sx={{ color: 'secondary.main' }} />;
    case 'Product Backlog Item':
      return <PBIIcon fontSize="small" sx={{ color: 'info.main' }} />;
    case 'Bug':
      return <BugIcon fontSize="small" sx={{ color: 'error.main' }} />;
    case 'Task':
      return <TaskIcon fontSize="small" sx={{ color: 'info.main' }} />;
    default:
      return <TaskIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
  }
}

function getWorkItemColor(workItemType) {
  switch (workItemType) {
    case 'Epic':
      return 'primary.main';
    case 'Feature':
      return 'secondary.main';
    case 'Product Backlog Item':
      return 'info.main';
    case 'Bug':
      return 'error.main';
    case 'Task':
      return 'info.main';
    default:
      return 'text.secondary';
  }
}

function getChipColor(workItemType) {
  const map = {
    Epic: 'primary',
    Feature: 'secondary',
    'Product Backlog Item': 'success',
    Bug: 'error',
    Task: 'info',
  };
  return map[workItemType] || 'default';
}

/**
 * Flat table of orphan work items – same columns as WorkItemsTreeView (hierarchy table).
 */
const OrphanItemsTable = ({ items = [] }) => {
  if (!items || items.length === 0) {
    return (
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          No items in this category.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width="50px" />
            <TableCell width="80px">ID</TableCell>
            <TableCell>Title</TableCell>
            <TableCell width="120px">Type</TableCell>
            <TableCell width="120px">State</TableCell>
            <TableCell width="120px">Target Date</TableCell>
            <TableCell width="120px">Team</TableCell>
            <TableCell width="100px">Story Points</TableCell>
            <TableCell width="100px">Completion</TableCell>
            <TableCell width="80px">Children</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow
              key={item.id}
              hover
              sx={{
                '&:hover': { backgroundColor: 'action.hover' },
                backgroundColor: 'grey.50',
              }}
            >
              <TableCell sx={{ paddingLeft: '8px' }}>
                <Box width={24} height={24} />
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  {getWorkItemIcon(item.workItemType)}
                  <Typography
                    component="a"
                    href={`${ADO_BASE_URL}/${item.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: getWorkItemColor(item.workItemType),
                      textDecoration: 'none',
                      marginLeft: 1,
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                        color: getWorkItemColor(item.workItemType),
                      },
                    }}
                  >
                    {item.id}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{item.title || 'No title'}</Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={item.workItemType || 'Unknown'}
                  size="small"
                  color={getChipColor(item.workItemType)}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <StatusBadge status={item.state} />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {item.targetDate ? formatDate(item.targetDate) : '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{item.team || '-'}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {item.storyPoints != null && item.storyPoints !== '' ? item.storyPoints : '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <ProgressBar percentage={0} showLabel={true} style={{ width: '100%' }} />
              </TableCell>
              <TableCell>
                <Typography variant="body2">-</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OrphanItemsTable;
